// The public pages' data layer (Module 09 §4.10, slice P3-C-1).
//
// Two doors, both unauthenticated, both reached from a link in a Telegram
// message. Everything here is deliberately unlike the office hooks:
//
//  * NO RETRY ON THE READ. The office retries because a manager will wait; a
//    customer on a phone will not, and a retry storm against a rate-limited
//    public door (§4.10: 30/min per IP) turns one slow load into a 429 for
//    everyone behind that NAT. One attempt, then the retry BUTTON — the
//    customer decides.
//  * NO CACHE ACROSS TOKENS. `gcTime: 0` and a token-keyed query: a shared
//    laptop must not hold another buyer's prices in memory after the tab is
//    closed. This is the same reasoning as the floor device's sign-out purge.
//  * ERRORS ARE VALUES, NOT THROWS. The screens switch over a total state
//    (lib/public-view.ts), so `throwOnError` is never armed and a 404 never
//    reaches an error boundary that would paint the office's chrome.
//  * The ACCEPT mutation sends an Idempotency-Key even though the contract
//    does not demand one from a browser: the double-tap on a phone is the
//    §9.1 case, and the key makes the second tap return the FIRST tap's order
//    instead of racing the token's single-use update. The key lives in
//    component STATE, minted once on mount — see the hook for why "once per
//    render" is not "once".
//  * A 410 IS NOT A FAILURE TO REPORT. All three gone codes the two doors
//    raise (TOKEN_USED, TOKEN_EXPIRED, TOKEN_SUPERSEDED) say the same thing:
//    the token is spent and the quote has moved into one of §4.10's OWN
//    designed pages. Both mutations therefore re-resolve the read instead of
//    raising an alert over an offer that no longer exists — `reResolveOnGone`.
//  * THE CONFIRM-RECEIVED DOOR (P3-D PR-D3, D-286) ANSWERS THE PAGE ITSELF,
//    and the page keeps it. `POST …/confirm-received` returns the tracking
//    payload after the confirmation, so the order read's cache is REPLACED
//    with that body rather than invalidated into a GET — the GET shares the
//    same 30/min per-IP counter as the press, and a re-fetch the customer did
//    not ask for is a 429 they would have to explain to their manager. The
//    replay rule is the server's (identical 200 while already delivered), so
//    a second tap is harmless whichever key it carries.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { client } from "../../../lib/api";
import { RequestError, newIdempotencyKey, unwrap } from "../../../lib/request";
import {
  orderViewFromError,
  quoteViewFromError,
  type PublicOrderPayload,
  type PublicOrderView,
  type PublicQuoteView,
} from "../lib/public-view";

export const publicKeys = {
  quote: (token: string) => ["public", "quote", token] as const,
  order: (token: string) => ["public", "order", token] as const,
};

/** §9.1's gone status. One number, three codes, one meaning: the token is
 * spent and the quote is in a state §4.10 already has a page for. */
const GONE = 410;

/**
 * Re-resolve the public quote read when a mutation answers 410.
 *
 * WHY IT IS AWAITED, WHICH IS THE WHOLE OF THE FIX. `useMutation` awaits
 * `onError` BEFORE it dispatches the error state to the component (query-core
 * `Mutation.execute`), so by the time the screen can see `accept.error` the
 * read has already been replaced with what the door now says — the accepted
 * page carrying the winner's order number and tracking link, or the expired or
 * superseded page. The screen never renders a frame of the OFFER under a token
 * that can no longer buy anything.
 *
 * That frame was the defect (P3-C-1 audit row 37). A 410 set a flag, the
 * screen printed a one-line alert INSIDE the still-valid page, and Accept,
 * Decline and the confirm Yes stayed pressable over a dead token — while the
 * order number that §4.10's already-accepted state and §9.1 ("the customer
 * still lands on their order — the 410 carries it") both promise appeared
 * nowhere on the page. D-263's same-client replay does not cover this: that
 * returns the winning 200 only when the retry carries the SAME idempotency
 * key, and a second device or a reloaded page mints a fresh key and still
 * gets the 410.
 *
 * A non-410 failure is left alone — it is a failure, and the screen says so.
 */
async function reResolveOnGone(
  queryClient: QueryClient,
  token: string,
  error: unknown,
): Promise<void> {
  if (!(error instanceof RequestError) || error.httpStatus !== GONE) return;
  await queryClient.invalidateQueries({ queryKey: publicKeys.quote(token) });
}

/** Shared posture for both doors — see the header for why each line is here. */
const PUBLIC_READ = {
  retry: false,
  gcTime: 0,
  staleTime: 0,
  refetchOnWindowFocus: false,
} as const;

export function usePublicQuote(token: string) {
  return useQuery<PublicQuoteView>({
    queryKey: publicKeys.quote(token),
    enabled: token.length > 0,
    ...PUBLIC_READ,
    queryFn: async ({ signal }): Promise<PublicQuoteView> => {
      try {
        const res = await client.GET("/v1/public/quotes/{token}", {
          signal,
          params: { path: { token } },
        });
        // The fallback title never reaches a customer's eyes — the screens
        // render their own copy per state — but it is what a thrown error
        // carries into the console and the Playwright trace.
        return unwrap(res, "public quote read failed");
      } catch (error) {
        return quoteViewFromError(error);
      }
    },
  });
}

export function usePublicOrder(publicToken: string) {
  return useQuery<PublicOrderView>({
    queryKey: publicKeys.order(publicToken),
    enabled: publicToken.length > 0,
    ...PUBLIC_READ,
    queryFn: async ({ signal }): Promise<PublicOrderView> => {
      try {
        const res = await client.GET("/v1/public/orders/{publicToken}", {
          signal,
          params: { path: { publicToken } },
        });
        return unwrap(res, "public order read failed");
      } catch (error) {
        return orderViewFromError(error);
      }
    },
  });
}

export function useAcceptPublicQuote(token: string) {
  // ONE key for the whole component lifetime, not one per attempt: a customer
  // whose first tap timed out and who taps again is retrying THE SAME accept,
  // and a fresh key would let the server treat it as a second order attempt.
  //
  // It is `useState` with a lazy initialiser, and the mechanism is the point.
  // A bare `const key = newIdempotencyKey()` in the hook body is minted on
  // EVERY render, and `useMutation` re-applies its options each render and
  // builds each attempt from the options current at `mutate` time — so a
  // re-render between taps (the screen's own `setConfirming`, or the
  // `isError` the failed first tap raises before any retry) silently gave the
  // retry a different key. That was the shape this hook shipped in, under this
  // same comment; a comment is not a mechanism. The initialiser runs once per
  // mount, and the key survives every re-render until the page is left.
  const [key] = useState(() => newIdempotencyKey());
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await client.POST("/v1/public/quotes/{token}/accept", {
        params: { path: { token }, header: { "Idempotency-Key": key } },
      });
      return unwrap(res, "public quote accept failed");
    },
    onError: (error) => reResolveOnGone(queryClient, token, error),
  });
}

export function useDeclinePublicQuote(token: string) {
  // Same one-key-per-mount rule as the accept above, for the same reason.
  const [key] = useState(() => newIdempotencyKey());
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { reason_code?: "price" | "lead_time" | "stock" | "other" }) => {
      const res = await client.POST("/v1/public/quotes/{token}/decline", {
        params: { path: { token }, header: { "Idempotency-Key": key } },
        body,
      });
      return unwrap(res, "public quote decline failed");
    },
    // The decline door raises the same three gone codes, and a customer who
    // taps Decline on an offer somebody already accepted deserves the same
    // answer as one who taps Accept: the page it actually is now, not "We
    // could not record that. Please try again." over a token no retry can
    // reach. One rule, both doors — a file where one door re-resolves and its
    // twin does not is a file the next reader has to test to understand.
    onError: (error) => reResolveOnGone(queryClient, token, error),
  });
}

/**
 * §4.10's "confirm received" — `POST /v1/public/orders/{publicToken}/
 * confirm-received` (P3-D PR-D3, D-286 ruling 14), the platform's third
 * unauthenticated write. The customer's press is the SOURCE OF TRUTH for
 * delivery: every shipped shipment and the order go `delivered` under it.
 *
 * ONE key per mount, the accept door's rule and mechanism (the lazy `useState`
 * initialiser — see `useAcceptPublicQuote` for why a bare call in the hook
 * body is one key per RENDER). The contract makes the header OPTIONAL on this
 * door because the server is idempotent BY STATE (an already-delivered order
 * answers the identical 200), so the key is a courtesy that keeps a double
 * tap from being two writes on the wire — not the thing that makes the second
 * tap safe.
 *
 * ON SUCCESS THE ORDER READ IS REPLACED, NOT REFETCHED (the header's last
 * bullet): the response IS the tracking payload after the confirmation, with
 * the Delivered step in `timeline` and `can_confirm_received: false`, so the
 * screen re-renders from it and the box withdraws itself. A failure is left
 * for the screen to say; there is no 410 on this door and nothing to
 * re-resolve — the order is where it was, and the remedy is the same press.
 */
export function usePublicOrderConfirmReceived(publicToken: string) {
  const [key] = useState(() => newIdempotencyKey());
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<PublicOrderPayload> => {
      const res = await client.POST("/v1/public/orders/{publicToken}/confirm-received", {
        params: { path: { publicToken }, header: { "Idempotency-Key": key } },
      });
      return unwrap(res, "public order confirm-received failed");
    },
    onSuccess: (payload) => {
      queryClient.setQueryData<PublicOrderView>(publicKeys.order(publicToken), payload);
    },
  });
}
